import { sendMessage, Message, MessageTimeout, registerAppError, AppError, getAppErrorMessage } from './Communication';

// Submodules
import { Omit } from '../utils/Types';

export let autoRotate: boolean = true;

interface AppAction<T> extends Message<T> {
    type: 'appaction';
}

type AppActionWithoutAgrument = Omit<AppAction<undefined>, 'arguments'>;

interface BackgroundContentSynchronizationArgument {
    url: string;
    path: string;
}

export function backgroundContentSynchronization(url: string, path: string): Promise<void> {
    return sendMessage<AppAction<BackgroundContentSynchronizationArgument>>({
        type: 'appaction',
        action: 'background_content_synchronization',
        arguments: {
            url: url,
            path: path
        }
    });
}

function doNothing(): void {
    // Used to ignore exceptions
}

// Note: right now we have to cancle the background content synchronization via
// an invalid request
export const cancleBackgroundContentSynchronization: () => Promise<void> = (): Promise<void> => backgroundContentSynchronization('', '').catch(doNothing);

type StatusbarVisibilities = 'show' | 'hide';

function setStatusbarVisibility(visibility: StatusbarVisibilities): Promise<void> {
    return sendMessage<AppAction<StatusbarVisibilities>>(
        {
            type: 'appaction',
            action: 'statusbar',
            arguments: visibility
        },
        true
    );
}

export const showStatusbar: () => Promise<void> = (): Promise<void> => setStatusbarVisibility('show');
export const hideStatusbar: () => Promise<void> = (): Promise<void> => setStatusbarVisibility('hide');

type StatusbarStyles =  'dark' | 'light';

/**
 * This message can be used to change the status bar style.
 */
function statusbarRequest(mode: StatusbarStyles): Promise<void> {
    return sendMessage<AppAction<StatusbarStyles>>(
        {
            type: 'appaction',
            action: 'statusbar_style',
            arguments: mode
        },
        true
    );
}

export const setLightStatusbar: () => Promise<void> = (): Promise<void> => statusbarRequest('light');
export const setDarkStatusbar: () => Promise<void> = (): Promise<void> => statusbarRequest('dark');

type FullscreenAction = 'enter' | 'leave';

function fullscreenRequest(mode: 'enter' | 'leave'): Promise<void> {
    return sendMessage<AppAction<FullscreenAction>>(
        {
            type: 'appaction',
            action: 'fullscreen',
            arguments: mode
        },
        true
    );
}

export const enterFullscreen: () => Promise<void> = (): Promise<void> => fullscreenRequest('enter');
export const leaveFullscreen: () => Promise<void> = (): Promise<void> => fullscreenRequest('leave');

interface RequestProgress {
    time: number;
    request_id: number;
}

interface DownloadProgress {
    size: number;
    fetched: number;
}

export interface DownloadRequestProgress extends RequestProgress {
    downloads: Array<DownloadProgress>;
}

@registerAppError('f030fe5b-4b3d-4a93-90f9-b24ba382882a')
export class DownloadCancled extends AppError {
    constructor(message: string) {
        super(message);

        Object.setPrototypeOf(this, DownloadCancled.prototype);
    }
}

interface CancelDownloadActionArgument {
    request_id: number;
    error_message: string;
}

export function cancelDownload(requestId: number): void {
    return void sendMessage<AppAction<CancelDownloadActionArgument>>(
        {
            type: 'appaction',
            action: 'cancel_download',
            arguments: {
                request_id: requestId,
                error_message: getAppErrorMessage(DownloadCancled, 'Download cancled')
            }
        },
        true
    );
}

export interface PreviewFile {
    url: string;
    mimetype: string;
    title: string;
}

export interface PreviewActionArgument {
    index: number;
    files: Array<PreviewFile>;
}

export function downloadPercentageReducer(acc: number | null, download: DownloadProgress): number {
    // Note: -1 is send if the size is unknown
    if (download.size < 1 || isNaN(acc!)) {
        return NaN;
    }

    const percentage: number = download.fetched / download.size;

    if (acc == null) {
        return percentage;
    }

    return (acc + percentage) / 2;
}

export interface PreviewRequest {
    promise: Promise<void>;
    cancel: () => void;
}

// Note: the `percentage` value of the `progressCallback` can be `NaN`
export function showPreview(arg: PreviewActionArgument, progressCallback?: (percentage: number) => void): PreviewRequest {
    let transactionId: number;
    const promise: Promise<void> = sendMessage<AppAction<PreviewActionArgument>, void, DownloadRequestProgress>(
        {
            type: 'appaction',
            action: 'preview',
            arguments: arg
        },
        false,
        MessageTimeout,
        {
            transactionId: (value: number): void => {
                transactionId = value;
            },
            progress: progressCallback == null ? undefined : (data: DownloadRequestProgress): void => {
                progressCallback(data.downloads.reduce(downloadPercentageReducer, null)! * 100);
            }
        }
    );

    return {
        promise,
        cancel: (): void => {
            return cancelDownload(transactionId);
        }
    };
}

function SFSafariViewController(url: string): Promise<void> {
    return sendMessage<AppAction<string>>({
        type: 'appaction',
        action: 'SFSafariViewController',
        arguments: url
    });
}

export const openSFSafariViewController: (url: string) => Promise<void> = (url: string): Promise<void> => SFSafariViewController(url);
export const closeSFSafariViewController: () => Promise<void> = (): Promise<void> => SFSafariViewController('');

export function minimize(): Promise<void> {
    return sendMessage<AppActionWithoutAgrument>({
        type: 'appaction',
        action: 'minimize'
    });
}

export function showKeyboard(): Promise<void> {
    return sendMessage<AppActionWithoutAgrument>({
        type: 'appaction',
        action: 'show_keyboard'
    });
}

/**
 * This message can be used to change the status bar color.
 * @param color hex-code
 */
export function setStatusbarColor(color: string): Promise<void> {
    // app-api expects hex-code without hash
    if (color.startsWith('#')) {
        color = color.substr(1);
    }

    // app-Api accepts only 6 digit hex-code
    if (color.length === 3) {
        color = `${color[0]}${color[0]}${color[1]}${color[1]}${color[2]}${color[2]}`;
    }

    return sendMessage<AppAction<string>>(
        {
            type: 'appaction',
            action: 'statusbar_color',
            arguments: color,
        },
        true
    );
}

/**
 * This message can be used for enable/disable the autorotation.
 * Note: the app should start with the default autorotate state.
 * @param value
 */
export function enableAutorotate(value: boolean): Promise<void> {
    autoRotate = value;
    return sendMessage<AppAction<'enable' | 'disable'>>(
        {
            type: 'appaction',
            action: 'autorotate',
            arguments: (value ? 'enable' : 'disable')
        },
        true
    );
}

export type OrientationMode = 'portrait' | 'landscape' | 'all';

/**
 * This message can be used for change the orientation mode.
 * Note: the app should start with the default orientation mode.
 * Allowed values are:
 *  portrait: portrait orientation mode
 *  landscape: landscape orientation mode
 *  all: portrait and landscape orientations mode
 * @param value
 */
export function setOrientationMode(value: OrientationMode): Promise<void> {
    return sendMessage<AppAction<OrientationMode>>(
        {
            type: 'appaction',
            action: 'orientation_mode',
            arguments: value
        },
        true
    );
}

export interface XLSXImage {
    row: number;
    col: number;
    path: string;
    maxwidth: number;
    maxheight: number;
}

interface XLSXEditingRequest {
    xlsx_file: string;
    images: Array<XLSXImage>;
}

export function extendXlsx(xlsxPath: string, images: Array<XLSXImage>): Promise<void> {
    return sendMessage<AppAction<XLSXEditingRequest>>({
        type: 'appaction',
        action: 'xlsx_editing',
        arguments: {
            xlsx_file: xlsxPath,
            images
        }
    });
}

export interface ArPreviewArguments {
    url: string;
}

export async function arPreview(url: string): Promise<void> {
    return sendMessage<AppAction<ArPreviewArguments>>(
        {
            type: 'appaction',
            action: 'ar_preview',
            arguments: { url },
        },
        true
    );
}

export interface PDFImage {
    uri: string;
    width: number;
    height: number;
    hasDimensions: boolean;
}

export interface PDFArticleInformation {
    index: string;
    quantity: number;
    singlePrice: string;
    totalPrice: string;
    label: string;
    subLabel: string;
    description: string;
    images: Array<PDFImage>;
}

export interface PDFListInformation {
    items: Array<PDFArticleInformation>;
    totalPrice: string;
    listFooter: string;
}

export interface PDFCreateArgs {
    path: string;
    template: 'articlelist' | 'productsheet';
}

export type PDFContentInformation = {
    // Header
    logo?: PDFImage;
    address: string;
    subject: string;
    correspondence: string;
    showPrices: boolean;
} & PDFListInformation;

export type PDFCreateTemplate = 'articlelist' | 'productsheet';

export type PDFCreateArg = {
    path: string;
    template: PDFCreateTemplate;
    translations: {[key: string]: string};
} & PDFContentInformation;

export function createPdf(value: PDFCreateArg): Promise<void> {
    return sendMessage<AppAction<PDFCreateArg>>(
        {
            type: 'appaction',
            action: 'create_pdf',
            arguments: value
        },
        false
    );
}

export interface MailAttachment {
    path: string;
    name: string;
    mimeType: string;
}

export interface MailOptions {
    recipients?: string;
    subject: string;
    body: string;
    html: boolean;
    attachments?: Array<MailAttachment>;
}

export function composeMail(options: MailOptions): Promise<void> {
    return sendMessage<AppAction<MailOptions>>(
        {
            type: 'appaction',
            action: 'compose_mail',
            arguments: options
        },
        true
    );
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/app-api/AppAction.ts